import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  updateRiskPermission,
  getRiskPermissionItem,
} from "store/risk/permissions/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import CompactMultiSelectDropdown from "components/dropdowns/CompactMultiSelectDropdown/CompactMultiSelectDropdown";
import styles from "./RiskPermissionEditModal.module.scss";
import { Modal } from "components/Modal/Modal";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { formatDateTime, useTimeFormat } from "helpers/timeFormat";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const RiskPermissionEditModal: React.FC = () => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const payload = useAppSelector((store) => store.risk.payload);
  const isItemFetching = useAppSelector((store) => store.risk.isItemFetching);
  const permissions = usePermissionHelper();

  useEffect(() => {
    if (entityId) {
      dispatch(getRiskPermissionItem(entityId));
    }
  }, [entityId]);

  const permissionMap = {
    canDeposit: { label: "Deposit", field: "deposit" },
    canWithdraw: { label: "Withdraw", field: "withdraw" },
    canPlayCasino: { label: "Casino", field: "casino" },
    canPlaySport: { label: "Sport", field: "sport" },
    canPlayPoker: { label: "Poker", field: "poker" },
    canUseBonus: { label: "Bonus", field: "bonus" },
    canUseCashback: { label: "Cashback", field: "cashback" },
    canUsePromoCode: { label: "Promo Code", field: "promoCode" },
    canJoinTournament: { label: "Tournament", field: "tournament" },
    canTransfer: { label: "Transfer", field: "transfer" },
    canCryptoConvert: { label: "Crypto Convert", field: "cryptoConvert" },
    canUseAffiliateFeatures: {
      label: "Affiliate Features",
      field: "affiliateFeatures",
    },
  } as const;

  const handleData = (key: string, value: boolean) => {
    dispatch(handleUpdateFields({ [key]: value }));
  };

  const handleCloseClick = () => {
    navigate(location.pathname);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!payload) return;

    const data = {
      canDeposit: payload.deposit ? 1 : 0,
      canWithdraw: payload.withdraw ? 1 : 0,
      canPlayCasino: payload.casino ? 1 : 0,
      canPlaySport: payload.sport ? 1 : 0,
      canPlayPoker: payload.poker ? 1 : 0,
      canUseBonus: payload.bonus ? 1 : 0,
      canUseCashback: payload.cashback ? 1 : 0,
      canUsePromoCode: payload.promoCode ? 1 : 0,
      canJoinTournament: payload.tournament ? 1 : 0,
      canTransfer: payload.transfer ? 1 : 0,
      canCryptoConvert: payload.cryptoConvert ? 1 : 0,
      canUseAffiliateFeatures: payload.affiliateFeatures ? 1 : 0,
      // Absent on older APIs: omit it until the backend supports the extension.
      ...(payload.permissionConditions !== undefined
        ? { permissionConditions: payload.permissionConditions }
        : {}),
      isUserBlocked: payload.userBlocked ? 1 : 0,
      isActive: payload.active ? 1 : 0,
    };

    const result = await dispatch(
      updateRiskPermission({
        riskLevel: payload.riskLevel,
        data,
      }),
    );

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );

    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("unableToSavePermission"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <Modal onClose={handleCloseClick} size="md" className={styles.editor}>
      {!payload ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Header>
            {t("editPermission")} - {payload.riskLevel}
          </Modal.Header>

          <Modal.Content className={styles.content}>
            <div className={styles.general}>
              <div className="risk-modal__general">
                <div className="risk-modal__general-inputs">
                  <InnerInput
                    value={payload.riskLevel}
                    label={t("riskLevel")}
                    type="text"
                    id="riskLevel"
                    onChange={() => {}}
                    disabled
                  />

                  <InnerInput
                    value={
                      payload.updatedAt
                        ? formatDateTime(payload.updatedAt, timeFormat)
                        : ""
                    }
                    label={t("updatedAt")}
                    type="text"
                    id="updatedAt"
                    onChange={() => {}}
                    disabled
                  />
                </div>

                <div className="risk-modal__general-switches">
                  <SwitchBtn
                    activeTxt={t("active")}
                    isActive={payload.active}
                    handleClick={() => handleData("active", !payload.active)}
                    disabled={!permissions?.update}
                  />

                  <SwitchBtn
                    activeTxt={t("userBlockedSwitcher")}
                    isActive={payload.userBlocked}
                    handleClick={() =>
                      handleData("userBlocked", !payload.userBlocked)
                    }
                    isRed
                    disabled={!permissions?.update}
                  />
                </div>
              </div>
            </div>

            <div className={styles.permissions}>
              <div className="risk-modal__title">Permissions</div>

              <div className={styles.tableWrap}>
                <table className={styles.permissionsTable}>
                  <thead>
                    <tr>
                      <th>Permission</th>
                      <th>Enabled</th>
                      <th>Additional conditions</th>
                      <th>Effective rule</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(permissionMap).map(([apiKey, config]) => {
                      const permission =
                        config.field === "affiliateFeatures"
                          ? "affiliate"
                          : config.field;
                      const conditions =
                        payload.permissionConditions?.[permission]
                          ?.conditions ?? [];
                      const options = [
                        { value: "kyc_verified", label: "KYC verified" },
                      ];
                      const conditionLabels = conditions.map(
                        (key) =>
                          options.find((option) => option.value === key)
                            ?.label ?? key,
                      );
                      const ruleSummary = payload[config.field]
                        ? conditionLabels.length
                          ? `Enabled AND ${conditionLabels.join(" AND ")}`
                          : "Enabled · No conditions"
                        : "Disabled";
                      return (
                        <tr key={apiKey}>
                          <th scope="row">{config.label}</th>
                          <td>
                            <Checkbox
                              value={payload[config.field]}
                              txt={`${config.label} enabled`}
                              id={apiKey}
                              onCheck={(v) => handleData(config.field, v)}
                              disabled={!permissions?.update || isItemFetching}
                            />
                          </td>
                          <td>
                            <CompactMultiSelectDropdown
                              label={
                                conditions.length
                                  ? `${config.label} conditions`
                                  : "No conditions"
                              }
                              items={options}
                              selectedValues={conditions}
                              useFixedMenu
                              compact
                              displayMode="chips"
                              disabled={
                                !permissions?.update ||
                                isItemFetching ||
                                payload.permissionConditions === undefined
                              }
                              onChange={(values) =>
                                dispatch(
                                  handleUpdateFields({
                                    permissionConditions: {
                                      ...payload.permissionConditions,
                                      [permission]: {
                                        conditions: values.map(String),
                                      },
                                    },
                                  }),
                                )
                              }
                            />
                          </td>
                          <td className={styles.summary} title={ruleSummary}>
                            {ruleSummary}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className={styles.note}>
                Rules are evaluated by the backend. Manual player blocks take
                priority.
              </p>
            </div>
          </Modal.Content>

          {permissions?.update && (
            <Modal.Footer className={styles.footer}>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />
              <Button
                isPrimary
                txt="Save"
                handleClick={handleApply}
                isLoad={isItemFetching}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default RiskPermissionEditModal;
